<template>
  <div id="buy-get-free" class="align-start fill-height">
    <!-- Loading Overlay -->
    <i-overlay
      v-if="loading && $vuetifyDisplay.smAndDown"
      text="Loading Buy Get Free promotions..."
    />
    <!-- Main Content -->
    <v-row v-else class="g-container-large" no-gutters>
      <!-- Dialogs -->
      <v-dialog
        v-if="dialog"
        v-model="dialog"
        max-width="940px"
        persistent
        @click:outside="reset"
        @keydown.esc="reset"
      >
        <i-edit-buy-get-free-promotion
          :family-groups="familyGroups"
          :products="enrichedProducts"
          :buy-get-free-promotion="editableBuyGetFreePromotion"
          :all-buy-get-promotions="buyGetFreePromotion"
          @cancel="reset"
          @save="reload"
        />
      </v-dialog>
      <v-dialog
        v-if="confirmationDialog"
        v-model="confirmationDialog"
        width="400px"
        @click:outside="resetDeletion"
        @keydown.esc="resetDeletion"
      >
        <i-confirmation-dialog
          text="Confirm deletion of this Buy Get Free promotion"
          action-text="Delete"
          :submitting="deletingBuyGetFreePromotion"
          @cancel="resetDeletion"
          @confirm="confirmDeletion"
        />
      </v-dialog>
      <!-- Section Title -->
      <v-col cols="12">
        <h2 class="ma-0">{{ $route.meta.title }}</h2>
        <!-- Action Buttons -->
        <template>
          <v-row>
            <v-col cols="12" md="3" lg="2">
              <v-btn
                large
                rounded
                class="i-btn-block-secondary g-full-width"
                @click="addNewPromotion"
              >
                Add Buy Get Free
              </v-btn>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" md="3" lg="2">
              <i-instructions
                instructionKey="data-maintenance/buy-get-free"
              ></i-instructions>
            </v-col>
          </v-row>
        </template>
        <!-- Tabs -->
        <template>
          <v-row>
            <v-col cols="6" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{ 'i-btn-select-active': selectedTab === 'ACTIVE' }"
                @click="selectedTab = 'ACTIVE'"
              >
                Active
              </v-btn>
            </v-col>
            <v-col cols="6" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{ 'i-btn-select-active': selectedTab === 'UPCOMING' }"
                @click="selectedTab = 'UPCOMING'"
              >
                Upcoming
              </v-btn>
            </v-col>
            <v-col cols="6" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{ 'i-btn-select-active': selectedTab === 'PAST' }"
                @click="selectedTab = 'PAST'"
              >
                Past
              </v-btn>
            </v-col>
            <v-spacer></v-spacer>
            <v-col class="pl-lg-8 promotion-table-actions" cols="12" lg="4">
              <div class="promotion-table-actions__inner">
                <i-local-filter
                  :filterPlaceholder="'Search by Name, Target, or Dates'"
                  @filter="localFilter"
                />
                <i-table-refresh-button
                  :loading="loading"
                  @refresh="refreshCurrentTable"
                />
              </div>
            </v-col>
          </v-row>
        </template>
        <!-- Tab Content -->
        <v-row>
          <v-col cols="12">
            <template v-if="!loading && filteredPromotions.length === 0">
              <i-empty-results
                hideButton
                title="No Active Buy Get Free promotions found."
              />
            </template>
            <template v-else>
              <v-data-table
                class="pt-2 elevation-1 i-data-table"
                :headers="headers"
                :items="filteredPromotions"
                :items-per-page="-1"
                :loading="loading"
                :no-data-text="emptyFilteredResultsText"
                :no-results-text="emptyFilteredResultsText"
                item-key="id"
                v-model:expanded="expandedBuyGetFreePromotion"
                :show-expand="!$vuetifyDisplay.smAndDown"
              >
                <template v-slot:[`item.supplierItemCodes`]="{ item }">
                  <span
                    v-if="
                      item.supplierItemCodes && item.supplierItemCodes.length
                    "
                  >
                    <span
                      v-for="(code, index) in item.supplierItemCodes"
                      :key="index"
                    >
                      {{ code }}<br />
                    </span>
                  </span>
                  <span v-else>-</span>
                </template>

                <template v-slot:[`item.targetType`]="{ item }">
                  <v-chip
                    v-if="getTargetType(item) === 'NATIONAL'"
                    small
                    color="green"
                    class="rounded-pill text-capitalize"
                  >
                    <strong> NATIONAL </strong>
                  </v-chip>
                  <v-chip
                    v-if="getTargetType(item) === 'STATE'"
                    small
                    color="info"
                    class="rounded-pill text-capitalize"
                  >
                    <strong> STATE</strong>
                  </v-chip>
                  <v-chip
                    v-if="getTargetType(item) === 'BANNER_GROUP'"
                    small
                    color="accent"
                    class="rounded-pill text-capitalize"
                  >
                    <strong> BANNER </strong>
                  </v-chip>
                  <v-chip
                    v-if="getTargetType(item) === 'MULTI_STORE_OPERATOR'"
                    small
                    color="primary"
                    class="rounded-pill text-capitalize"
                  >
                    <strong> MSO </strong>
                  </v-chip>

                  <v-chip
                    v-if="getTargetType(item) === 'RETAILER_STORE'"
                    small
                    color="secondary"
                    class="rounded-pill text-capitalize"
                  >
                    <strong>{{ isILR ? 'CUSTOMERS' : 'RETAILERS' }}</strong>
                  </v-chip>
                </template>
                <template v-slot:[`item.targetValue`]="{ item }">
                  <span
                    v-if="
                      item.targetBannerGroups &&
                      item.targetBannerGroups.length > 0
                    "
                  >
                    <span
                      v-for="target in item.targetBannerGroups"
                      :key="target.bannerGroupId"
                    >
                      {{ target.displayName }}<br />
                    </span>
                  </span>
                  <span
                    v-else-if="
                      item.targetMultiStoreOperators &&
                      item.targetMultiStoreOperators.length > 0
                    "
                  >
                    <span
                      v-for="target in item.targetMultiStoreOperators"
                      :key="target.multiStoreOperatorId"
                    >
                      {{ target.displayName }}<br />
                    </span>
                  </span>
                  <span
                    v-else-if="
                      item.targetStores && item.targetStores.length > 0
                    "
                  >
                    <span
                      v-for="target in item.targetStores"
                      :key="target.storeId"
                    >
                      {{ target.displayNameWithZone }}<br />
                    </span>
                  </span>
                  <span v-else> - </span>
                </template>

                <template v-slot:[`item.fOrP`]="{ item }">
                  <div>
                    <div v-if="item.familyGroup">
                      <v-icon
                        class="i-promo-type-icon i-promo-type-icon-family-group"
                        v-tooltip.left="{
                          html: `familyGroupProductsToolTipContent-${item.id}`
                        }"
                      >
                        mdi-alpha-f-circle-outline
                      </v-icon>
                      <span
                        :id="`familyGroupProductsToolTipContent-${item.id}`"
                      >
                        <strong>Family Group</strong><br />
                        <span
                          v-for="productName in getFamilyGroupProducts(
                            item.familyGroup
                          )"
                          :key="productName"
                        >
                          {{ productName }}<br />
                        </span>
                      </span>
                    </div>
                    <!-- If productCode exists -->
                    <div v-else-if="item.productCode">
                      <v-icon
                        class="i-promo-type-icon i-promo-type-icon-single-product"
                        v-tooltip.left="'Single Product'"
                      >
                        mdi-alpha-p-circle-outline
                      </v-icon>
                    </div>
                    <div v-else-if="item.byRange">
                      <v-icon
                        class="i-promo-type-icon i-promo-type-icon-by-range"
                        v-tooltip.left="'Range Product'"
                      >
                        mdi-alpha-r-circle-outline
                      </v-icon>
                    </div>
                  </div>
                </template>
                <template #[`item.familyOrProductName`]="{ item }">
                  <template v-if="item.byRange">
                    <div class="range-products-dropdown">
                      <v-select
                        :items="formatRangeProducts(item.rangeProducts)"
                        dense
                        outlined
                        placeholder="Range Products"
                        style="width: 250px"
                      ></v-select>
                    </div>
                  </template>
                  <template v-else>
                    <router-link
                      v-if="!item.familyGroupDeleted && !item.byRange"
                      :to="getBuyGetLinkRoute(item)"
                      class="i-text-link"
                      :class="{ 'p-strike-through': item.toBeDeleted }"
                    >
                      {{ item.familyOrProductName }}
                    </router-link>
                    <span v-else>{{ item.familyOrProductName }} (DELETED)</span>
                  </template>
                </template>

                <template v-slot:[`item.maxFreeUnitsCap`]="{ item }">
                  <span v-if="item.maxFreeUnitsCap">
                    {{ item.maxFreeUnitsCap }}
                  </span>
                  <span v-else>-</span>
                </template>

                <template #[`item.startDate`]="{ item }">
                  {{ $filters.formatDateTime12h(item.startDateTime) }}
                </template>
                <template #[`item.endDate`]="{ item }">
                  {{ $filters.formatDateTime12h(item.endDateTime) }}
                </template>
                <template #[`item.actions`]="{ item }">
                  <v-btn
                    :disabled="selectedTab === 'PAST'"
                    color="secondary"
                    icon
                    v-tooltip="'Edit'"
                    @click="editPromotion(item)"
                  >
                    <v-icon>mdi-circle-edit-outline</v-icon>
                  </v-btn>
                  <v-btn
                    :disabled="
                      selectedTab === 'ACTIVE' || selectedTab === 'PAST'
                    "
                    color="error"
                    icon
                    v-tooltip="'Delete'"
                    @click="startDeletion(item)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <template #expanded-item="{ item }">
                  <td colspan="100%">
                    <v-simple-table dense class="full-width-table">
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="text-center" style="width: 25%">
                              TIER NUMBER
                            </th>
                            <th class="text-center" style="width: 25%">
                              BUY QUANTITY
                            </th>
                            <th class="text-center" style="width: 25%">
                              FREE QUANTITY
                            </th>
                            <th class="text-center" style="width: 25%">
                              {{ getFreeProductColumnLabel(item) }}
                            </th>
                          </tr>
                        </thead>
                        <tbody v-if="item.targetType === 'NATIONAL'">
                          <tr>
                            <th class="g-bold text-center" colspan="4">
                              NATIONAL
                            </th>
                          </tr>
                          <tr
                            v-for="(tier, index) in sortedTiers(item.tiers)"
                            :key="'tier-' + tier.tierId"
                          >
                            <td class="text-center">{{ index + 1 }}</td>
                            <td class="text-center">{{ tier.buyQty }}</td>
                            <td class="text-center">{{ tier.freeQty }}</td>
                            <td class="text-center">
                              {{ getFreeProductName(item) }}
                            </td>
                          </tr>
                        </tbody>
                        <tbody
                          v-for="state in groupedStates(item.tiers)"
                          :key="'state-' + state.state"
                        >
                          <!-- State Header -->
                          <tr>
                            <th class="g-bold text-center" colspan="4">
                              {{ state.state }}
                            </th>
                          </tr>
                          <!-- Tier Details for the State -->
                          <tr
                            v-for="(tier, index) in sortedTiers(state.tiers)"
                            :key="'tier-' + tier.tierId"
                          >
                            <td class="text-center">{{ index + 1 }}</td>
                            <td class="text-center p-cartons">
                              {{ tier.buyQty }}
                            </td>
                            <td class="text-center p-cartons">
                              {{ tier.freeQty }}
                            </td>
                            <td class="text-center">
                              {{ getFreeProductName(item) }}
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </td>
                </template>
              </v-data-table>
            </template>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import iOverlay from '@/components/Overlay'
import iConfirmationDialog from '@/components/ConfirmationDialog'
import iEditBuyGetFreePromotion from '../components/EditBuyGetFreePromotion'
import iLocalFilter from '@/components/LocalFilter'
import iEmptyResults from '@/components/EmptyResults'
import {
  fetchBuyGetFreePromotions,
  deleteBuyGetFreePromotion
} from '@/services/buyGetPromotion'
import { findProductsForSupplierAndProductCodes } from '@/services/products'
import { mapState, mapActions, mapGetters } from 'vuex'
import _ from 'lodash'
import iInstructions from '@/components/Instructions'
import iTableRefreshButton from '../components/TableRefreshButton'

export default {
  name: 'BuyGetFree',

  components: {
    iOverlay,
    iConfirmationDialog,
    iEditBuyGetFreePromotion,
    iLocalFilter,
    iEmptyResults,
    iInstructions,
    iTableRefreshButton
  },

  data: () => ({
    loading: true,
    dialog: false,
    confirmationDialog: false,
    deletingBuyGetFreePromotion: false,
    editingBuyGetFreePromotion: false,
    editableBuyGetFreePromotion: {},
    buyGetFreePromotion: [],
    pageProductsByCode: {},
    selectedTab: 'ACTIVE',
    expandedBuyGetFreePromotion: [],
    headers: [
      { text: 'TARGET TYPE', value: 'targetType', align: 'center' },
      { text: 'TARGET(S)', value: 'targetValue', align: 'center' },
      { text: 'TYPE', value: 'fOrP', align: 'center' },

      {
        text: 'SUPPLIER ITEM CODE',
        value: 'supplierItemCodes',
        align: 'center'
      },
      {
        text: 'FAMILY GROUP OR PRODUCT NAME',
        value: 'familyOrProductName',
        align: 'center',
        width: '30%'
      },

      {
        text: 'BUY GET FREE NAME',
        value: 'name',
        align: 'center',
        width: '20%'
      },
      {
        text: 'MAX CAP (Free Units)',
        value: 'maxFreeUnitsCap',
        align: 'center'
      },
      { text: 'START DATE', value: 'startDate', align: 'center' },
      { text: 'END DATE', value: 'endDate', align: 'center' },
      { text: 'ACTIONS', value: 'actions', align: 'center', sortable: false }
    ],
    filterText: ''
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapState('products', ['products']),
    ...mapState('familyGroups', ['familyGroups']),
    ...mapGetters('tenant', ['isILR']),
    ...mapState('bannerGroups', ['bannerGroups']),
    ...mapState('multiStoreOperators', ['multiStoreOperators']),
    ...mapState('stores', ['retailerStores']),

    enrichedProducts() {
      const productsByCode = new Map(
        this.products.map((product) => [product.productCode, product])
      )

      Object.values(this.pageProductsByCode).forEach((product) => {
        productsByCode.set(product.productCode, product)
      })

      return [...productsByCode.values()]
    },

    filteredPromotions() {
      return this.buyGetFreePromotion
        .filter((promotion) => promotion.status === this.selectedTab)
        .filter((promotion) => {
          if (!this.filterText) return true

          const name = promotion.name ? promotion.name.toLowerCase() : ''
          const targetType = promotion.targetType
            ? promotion.targetType.toLowerCase()
            : ''
          const familyGroupProductName = promotion.familyGroupProductName
            ? promotion.familyGroupProductName.toLowerCase()
            : ''
          const startDate = promotion.startDate
            ? this.$filters.formatDate(promotion.startDate).toLowerCase()
            : ''
          const endDate = promotion.endDate
            ? this.$filters.formatDate(promotion.endDate).toLowerCase()
            : ''

          const search = this.filterText.toLowerCase()

          return (
            name.includes(search) ||
            targetType.includes(search) ||
            familyGroupProductName.includes(search) ||
            startDate.includes(search) ||
            endDate.includes(search)
          )
        })
    },

    emptyFilteredResultsText() {
      return `No ${this.selectedTab} promotions found`
    }
  },

  methods: {
    ...mapActions('products', ['fetchProducts']),
    ...mapActions('familyGroups', ['fetchFamilyGroups']),

    async fetchPromotions() {
      this.loading = true
      try {
        const { data } = await fetchBuyGetFreePromotions(
          this.selectedSupplierId
        )
        await this.loadPageProducts(data)
        this.buyGetFreePromotion = data.map((promotion) => {
          const enriched = this.enrichTargetDisplayNames(promotion)
          return {
            ...enriched,
            supplierItemCodes: this.getSupplierItemCodesForPromotion(promotion),
            familyOrProductName: promotion.familyGroup
              ? promotion.familyGroup.name
              : promotion.productCode
                ? this.getProductByCode(promotion.productCode)
                    ?.tradeItemDescription || 'Unknown Product'
                : '',
            familyGroupId: promotion.familyGroup?.id || null,
            targetType: this.getTargetType(promotion)
          }
        })
      } catch (error) {
        this.$emit('show-notification', {
          color: 'error',
          text: error.message || 'Failed to fetch promotions.'
        })
      } finally {
        this.loading = false
      }
    },

    async confirmDeletion() {
      try {
        this.deletingBuyGetFreePromotion = true
        await deleteBuyGetFreePromotion(
          this.selectedSupplierId,
          this.editableBuyGetFreePromotion.id
        )
        this.$emit('show-notification', {
          color: 'success',
          text: 'Promotion deleted successfully!'
        })

        this.reload()
      } catch (error) {
        this.$emit('show-notification', {
          color: 'error',
          text: error.message || 'Failed to delete the promotion.'
        })
      } finally {
        this.deletingBuyGetFreePromotion = false
        this.resetDeletion()
      }
    },

    getBuyQtyByTierId(tierId) {
      for (const promotion of this.buyGetFreePromotion) {
        const tier = promotion.tiers.find((t) => t.tierId === tierId)
        if (tier) return tier.buyQty
      }
      return 0
    },

    getFreeQtyByTierId(tierId) {
      for (const promotion of this.buyGetFreePromotion) {
        const tier = promotion.tiers.find((t) => t.tierId === tierId)
        if (tier) return tier.freeQty
      }
      return 0
    },

    getFreeProductColumnLabel(promotion) {
      return promotion?.freeSelectionMode === 'RETAILER_CHOICE'
        ? 'FREE PRODUCT SELECTION'
        : 'FREE PRODUCT NAME'
    },

    getFreeProductName(promotion) {
      if (promotion?.freeSelectionMode === 'RETAILER_CHOICE') {
        return 'Retailer chooses from eligible products'
      }

      const product = this.getProductByCode(promotion?.freeProductCode)
      return product ? product.tradeItemDescription : 'Unknown Product'
    },

    getProductByCode(productCode) {
      return (
        this.pageProductsByCode[productCode] ||
        this.products.find((p) => p.productCode === productCode)
      )
    },

    // Buy Get Free promotions can reference product codes via
    // familyGroup.products, a flat productCodes array (family groups whose
    // products aren't nested inline), a single productCode, rangeProducts,
    // or freeProductCode. `this.products` (the Vuex module populated by a
    // separate, un-awaited fetchProducts() call in mounted()) isn't
    // guaranteed to be loaded - or to include every one of these codes -
    // by the time promotions are ready to render, which is what previously
    // made every Supplier Item Code lookup miss and permanently show
    // 'Unknown'. Batch-fetching exactly the codes this page's promotions
    // reference and awaiting it before finalizing the promotion list
    // (mirroring the fix already proven in VolumeBuys.vue) guarantees the
    // real codes are available on first render.
    async loadPageProducts(promotions) {
      const productCodes = [
        ...new Set(
          promotions.flatMap((promotion) => {
            const codes = []
            if (promotion.familyGroup?.products?.length) {
              codes.push(
                ...promotion.familyGroup.products.map((p) => p.productCode)
              )
            }
            if (promotion.productCodes?.length) {
              codes.push(...promotion.productCodes)
            }
            if (promotion.byRange && promotion.rangeProducts?.length) {
              codes.push(...promotion.rangeProducts)
            }
            if (promotion.productCode) codes.push(promotion.productCode)
            if (promotion.freeProductCode) codes.push(promotion.freeProductCode)
            return codes
          })
        )
      ].filter(Boolean)

      if (!productCodes.length) {
        this.pageProductsByCode = {}
        return
      }

      try {
        const products = await findProductsForSupplierAndProductCodes(
          this.selectedSupplierId,
          productCodes,
          { includeUpcoming: false }
        )

        this.pageProductsByCode = products.reduce((acc, product) => {
          acc[product.productCode] = product
          return acc
        }, {})
      } catch (e) {
        this.pageProductsByCode = {}
      }
    },

    localFilter(filterText) {
      this.filterText = filterText
    },

    addNewPromotion() {
      this.editableBuyGetFreePromotion = {}
      this.dialog = true
    },

    editPromotion(buyGetFreePromotion) {
      this.editableBuyGetFreePromotion = _.cloneDeep(buyGetFreePromotion)
      this.dialog = true
    },

    startDeletion(buyGetFreePromotion) {
      this.editableBuyGetFreePromotion = { ...buyGetFreePromotion }
      this.confirmationDialog = true
    },

    reset() {
      this.editableBuyGetFreePromotion = {}
      this.dialog = false
    },

    resetDeletion() {
      this.confirmationDialog = false
      this.editableBuyGetFreePromotion = {}
    },

    reload() {
      this.fetchPromotions()
      this.reset()
    },

    refreshCurrentTable() {
      this.reload()
    },

    groupedStates(tiers) {
      const stateGroups = {}

      tiers.forEach((tier) => {
        ;(tier.targetStates || []).forEach((state) => {
          if (!stateGroups[state.state]) {
            stateGroups[state.state] = {
              state: state.state,
              tiers: []
            }
          }
          stateGroups[state.state].tiers.push({
            buyQty: tier.buyQty,
            freeQty: tier.freeQty,
            tierId: tier.tierId
          })
        })
      })
      return Object.values(stateGroups)
    },

    sortedTiers(tiers) {
      return tiers.slice().sort((a, b) => a.tierId - b.tierId)
    },

    formatRangeProducts(rangeProducts) {
      return rangeProducts.map((code) => {
        const product = this.getProductByCode(code)
        return {
          text: product
            ? `${product.supplierItemId} / ${product.tradeItemDescription}`
            : `Unknown Product (${code})`,
          value: code
        }
      })
    },

    getBuyGetLinkRoute(item) {
      if (item.byRange) return null
      if (item.familyGroup) {
        return {
          name: 'family-groups',
          query: { familyGroupId: item.familyGroup.id }
        }
      } else if (item.productCode) {
        const product = this.getProductByCode(item.productCode)
        return {
          name: 'products',
          query: { supplierItemId: product?.supplierItemId }
        }
      }
      return '#'
    },

    getTargetType(promotion) {
      if (
        promotion.targetBannerGroups &&
        promotion.targetBannerGroups.length > 0
      ) {
        return 'BANNER_GROUP'
      } else if (
        promotion.targetMultiStoreOperators &&
        promotion.targetMultiStoreOperators.length > 0
      ) {
        return 'MULTI_STORE_OPERATOR'
      } else if (promotion.targetStores && promotion.targetStores.length > 0) {
        return 'RETAILER_STORE'
      } else if (
        !promotion.targetStates ||
        promotion.targetStates.length === 0
      ) {
        return 'NATIONAL'
      } else {
        return 'STATE'
      }
    },

    enrichTargetDisplayNames(promotion) {
      const enriched = { ...promotion }
      if (promotion.targetBannerGroups?.length) {
        enriched.targetBannerGroups = promotion.targetBannerGroups.map(
          (target) => {
            const match = this.bannerGroups.find(
              (bg) => bg.id === target.bannerGroupId
            )
            return {
              ...target,
              displayName: match?.displayName || target.bannerGroupName
            }
          }
        )
      }

      if (promotion.targetMultiStoreOperators?.length) {
        enriched.targetMultiStoreOperators =
          promotion.targetMultiStoreOperators.map((target) => {
            const match = this.multiStoreOperators.find(
              (mso) => mso.id === target.multiStoreOperatorId
            )
            return {
              ...target,
              displayName: match?.displayName || target.multiStoreOperatorName
            }
          })
      }

      if (promotion.targetStores?.length) {
        enriched.targetStores = promotion.targetStores.map((target) => {
          const match = this.retailerStores.find(
            (store) => store.id === target.storeId
          )
          return {
            ...target,
            displayNameWithZone: match
              ? `${target.storeName} (${
                  match.pricingRegionExternalCode ||
                  match.pricingRegionCode ||
                  'Unknown'
                })`
              : target.storeName
          }
        })
      }

      return enriched
    },

    getFamilyGroupProducts(familyGroup = {}) {
      if (!familyGroup || !familyGroup.id) return []

      const match = this.familyGroups.find((f) => f.id === familyGroup.id)
      if (!match || !match.productCodes?.length) return []

      return match.productCodes
        .map((code) => {
          const product = this.getProductByCode(code)
          return product
            ? product.tradeItemDescription
            : `Unknown Product (${code})`
        })
        .filter(Boolean)
    },
    getSupplierItemId(productCode) {
      const product = this.getProductByCode(productCode)
      return product ? product.supplierItemId : 'Unknown'
    },
    getSupplierItemCodesForPromotion(promotion) {
      let productCodes = []

      if (promotion.byRange && promotion.rangeProducts?.length) {
        productCodes = promotion.rangeProducts
      } else if (promotion.productCodes?.length) {
        productCodes = promotion.productCodes
      } else if (promotion.productCode) {
        productCodes = [promotion.productCode]
      }

      return productCodes.map((code) => this.getSupplierItemId(code))
    }
  },

  mounted() {
    this.fetchProducts({ supplierId: this.selectedSupplierId, all: true })
    this.fetchFamilyGroups(this.selectedSupplierId)
    this.fetchPromotions()
  }
}
</script>

<style lang="scss" scoped>
.promotion-table-actions__inner {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 42px;
  gap: 10px;
  align-items: stretch;
}

.p-cartons {
  font-weight: bold;
  color: $primary-color;
  font-size: 16px;
}
.i-promo-type-icon {
  margin-right: 5px;
}

.i-promo-type-icon-single-product {
  color: $primary-color;
}
.range-products-dropdown {
  display: inline-flex;
  margin: 20px;
  justify-content: center;
  align-items: center;
  width: 60%;
}
</style>
